import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchOnlineUsers, OnlineUser } from "services/presence.service";
import { isBelowDesktop } from "utils/breakpoints";

interface PresenceState {
  self: OnlineUser | null;
  online: OnlineUser[];
  offline: OnlineUser[];
  expanded: boolean;
  lastFetchedAt: number | null;
}

const STORAGE_KEY = "socialPanelExpanded";

function readExpanded(): boolean {
  // Below 1024px an expanded panel covers the page, so it never starts open.
  if (isBelowDesktop()) return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// Only the desktop rail's open/closed choice is remembered. Below 1024px the list is a sheet that
// opens and closes by itself (a tap on the top bar, a page change), and saving that would
// overwrite the choice made on a wide screen.
function rememberExpanded(expanded: boolean) {
  if (isBelowDesktop()) return;
  try {
    localStorage.setItem(STORAGE_KEY, expanded ? "1" : "0");
  } catch {
    /* ignore */
  }
}

const initialState: PresenceState = {
  self: null,
  online: [],
  offline: [],
  expanded: readExpanded(),
  lastFetchedAt: null,
};

export const fetchOnlineThunk = createAsyncThunk(
  "presence/fetchOnline",
  async (companyId: string) => fetchOnlineUsers(companyId)
);

const presenceSlice = createSlice({
  name: "presence",
  initialState,
  reducers: {
    toggleExpanded(state) {
      state.expanded = !state.expanded;
      rememberExpanded(state.expanded);
    },
    /** Someone started or finished a call. Pushed by the backend so the panel
     *  can say "they are talking" now, rather than up to a poll later. */
    callPresenceChanged(
      state,
      action: PayloadAction<{ userId: string; inCall: boolean }>
    ) {
      const { userId, inCall } = action.payload;
      const mark = (u: OnlineUser) => {
        if (String(u.id) === String(userId)) u.inCall = inCall;
      };
      state.online.forEach(mark);
      state.offline.forEach(mark);
      if (state.self) mark(state.self);
    },
    /** The screen became wide enough for the rail: it shows the remembered choice, not the sheet's state. */
    restoreExpanded(state) {
      state.expanded = readExpanded();
    },
    setExpanded(state, action: PayloadAction<boolean>) {
      state.expanded = action.payload;
      rememberExpanded(action.payload);
    },
    setPresenceSnapshot(
      state,
      action: PayloadAction<{
        self: OnlineUser | null;
        online: OnlineUser[];
        offline: OnlineUser[];
      }>
    ) {
      state.self = action.payload.self;
      state.online = action.payload.online;
      state.offline = action.payload.offline;
      state.lastFetchedAt = Date.now();
    },
    avatarChanged(
      state,
      action: PayloadAction<{ userId: string; avatarFileId: string | null }>
    ) {
      const { userId, avatarFileId } = action.payload;
      if (state.self && state.self.id === userId) {
        state.self.avatarFileId = avatarFileId;
      }
      const updateInList = (list: OnlineUser[]) => {
        const idx = list.findIndex((u) => u.id === userId);
        if (idx !== -1) list[idx] = { ...list[idx], avatarFileId };
      };
      updateInList(state.online);
      updateInList(state.offline);
    },
    /**
     * Real-time `user-online` event from the server: a colleague just
     * connected. Move them from offline → online (or insert if new).
     * Keep online list sorted by lastSeenAt DESC (newest first).
     */
    applyUserOnline(state, action: PayloadAction<{ user: OnlineUser }>) {
      const incoming = action.payload.user;
      // Don't echo into self (server skips, but defence in depth)
      if (state.self && state.self.id === incoming.id) return;
      // Drop from offline if there
      state.offline = state.offline.filter((u) => u.id !== incoming.id);
      // Update or insert into online
      const idx = state.online.findIndex((u) => u.id === incoming.id);
      if (idx === -1) {
        state.online.unshift(incoming);
      } else {
        state.online[idx] = { ...state.online[idx], ...incoming };
      }
    },
    /**
     * Real-time `user-offline` event: a colleague just disconnected.
     * Move them online → offline. Re-sort offline by lastSeenAt DESC
     * (newest first) so they appear at the top of the offline list.
     */
    applyUserOffline(state, action: PayloadAction<{ userId: string }>) {
      const { userId } = action.payload;
      if (state.self && state.self.id === userId) return;
      const idx = state.online.findIndex((u) => u.id === userId);
      if (idx === -1) return;
      const [user] = state.online.splice(idx, 1);
      // Server doesn't update lastSeenAt at the moment of disconnect, so use
      // the heartbeat that was last persisted; fall back to "now" if missing
      // so the offline list ordering stays sensible.
      const updated = { ...user, lastSeenAt: user.lastSeenAt || Date.now() };
      state.offline.unshift(updated);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOnlineThunk.fulfilled, (state, action) => {
      state.self = action.payload?.self || null;
      state.online = action.payload?.online || [];
      state.offline = action.payload?.offline || [];
      state.lastFetchedAt = Date.now();
    });
  },
});

export const {
  toggleExpanded,
  setExpanded,
  restoreExpanded,
  setPresenceSnapshot,
  avatarChanged,
  applyUserOnline,
  applyUserOffline,
  callPresenceChanged,
} = presenceSlice.actions;
export default presenceSlice.reducer;
